import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, EmptyState, ErrorState, Skeleton } from "../components/ui";
import { listPublicDogs, type DogSummary } from "../dogs/api";

function RecentDogs() {
  const [items, setItems] = useState<DogSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    listPublicDogs({ limit: 6 })
      .then((page) => {
        if (!active) return;
        setItems(page.items);
        setError(false);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (loading) return <Skeleton lines={3} />;
  if (error) return <ErrorState title="Không tải được chó mới" description="Vui lòng thử lại sau." />;
  if (items.length === 0) {
    return (
      <EmptyState
        title="Chưa có hồ sơ đã xuất bản"
        description="Hồ sơ đầu tiên sẽ xuất hiện tại đây sau khi được kiểm duyệt."
      />
    );
  }

  return (
    <div className="public-dog-grid">
      {items.map((dog) => (
        <Link key={dog.id} to={`/dogs/${dog.slug}`} className="public-dog-card">
          <div className="dog-avatar-placeholder" aria-hidden="true">
            {dog.registeredName.slice(0, 1).toUpperCase()}
          </div>
          <div>
            <h3>{dog.registeredName}</h3>
            <p>{dog.breed?.nameVi ?? "Chưa cập nhật giống"}</p>
            <small>{dog.callName ? `Tên gọi: ${dog.callName}` : "Hồ sơ đã xuất bản"}</small>
          </div>
        </Link>
      ))}
    </div>
  );
}

export function ProductHomePage() {
  return (
    <>
      <section className="product-hero">
        <div>
          <p className="eyebrow">WorkingDog VN</p>
          <h1>Hồ sơ và phả hệ đáng tin cậy cho working dog Việt Nam.</h1>
          <p className="product-hero__lead">
            Xây dựng danh tính thành viên xác thực, quản lý dữ liệu có nguồn gốc và hồ sơ chó có kiểm duyệt.
          </p>
          <div className="hero-actions">
            <Link className="ui-button ui-button--primary ui-button--md" to="/member/dogs/new">
              Đăng hồ sơ chó
            </Link>
            <Link className="ui-button ui-button--secondary ui-button--md" to="/dogs">
              Xem chó mới
            </Link>
          </div>
        </div>
        <aside className="trust-panel">
          <Badge tone="success">Cloudflare-native</Badge>
          <h2>Dữ liệu được kiểm soát từ đầu</h2>
          <ul>
            <li>Hồ sơ nháp không xuất hiện công khai.</li>
            <li>Hồ sơ chó được kiểm tra trùng trước khi gửi duyệt.</li>
            <li>Thông tin nhạy cảm không được trả qua public API.</li>
            <li>Mọi thay đổi quan trọng đều có lịch sử và audit.</li>
          </ul>
        </aside>
      </section>

      <section className="feature-section" aria-labelledby="recent-dogs-title">
        <header className="section-heading">
          <p className="eyebrow">Hồ sơ mới</p>
          <h2 id="recent-dogs-title">Chó được xuất bản gần đây</h2>
          <p>Danh sách lấy trực tiếp từ public Dog API và chỉ hiển thị hồ sơ đã được duyệt.</p>
        </header>
        <RecentDogs />
        <div className="hero-actions">
          <Link className="ui-button ui-button--primary ui-button--md" to="/member/dogs/new">
            Đăng hồ sơ chó
          </Link>
          <Link className="ui-button ui-button--secondary ui-button--md" to="/dogs">
            Xem tất cả hồ sơ
          </Link>
        </div>
      </section>

      <section className="feature-section" aria-labelledby="foundation-title">
        <header className="section-heading">
          <p className="eyebrow">Nền tảng dữ liệu</p>
          <h2 id="foundation-title">Sẵn sàng cho hệ sinh thái working dog</h2>
          <p>Các module nghiệp vụ được tách rõ để hồ sơ chó, phả hệ và ownership phát triển an toàn.</p>
        </header>
        <div className="feature-grid">
          <article className="feature-card">
            <span className="feature-index">01</span>
            <h3>Tài khoản xác thực</h3>
            <p>Session cookie HttpOnly, phân quyền và thu hồi phiên theo thiết bị.</p>
          </article>
          <article className="feature-card">
            <span className="feature-index">02</span>
            <h3>Hồ sơ có kiểm duyệt</h3>
            <p>Draft, duplicate detection, review queue và published edit request.</p>
          </article>
          <article className="feature-card">
            <span className="feature-index">03</span>
            <h3>Lịch sử có thể truy vết</h3>
            <p>Database versioned, audit append-only và transaction boundary cho mutation quan trọng.</p>
          </article>
        </div>
      </section>
    </>
  );
}
